import React, { useState, useEffect } from 'react';
import { Card as CardType, Comment, CardActivity, Attachment, commentApi, activityApi, cardApi, attachmentApi } from '../services/api';
import { AttachmentList } from './AttachmentList';
import { LabelManager } from './LabelManager';
import { CardAssignments } from './CardAssignments';
import { Xmark, EditPencil, Trash, Plus, Page, Check, BoxIso, Link, Download } from 'iconoir-react';
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf';

const LABEL_COLORS = [
  { value: '#75D494', name: 'Vert' },
  { value: '#EDD87A', name: 'Jaune' },
  { value: '#EDB85C', name: 'Orange' },
  { value: '#EDA8A1', name: 'Rouge/Rose' },
  { value: '#C7A8ED', name: 'Violet' },
  { value: '#85BFE0', name: 'Bleu' },
  { value: '#A3DBE8', name: 'Cyan' },
  { value: '#BED6A3', name: 'Vert clair' },
  { value: '#EDAABF', name: 'Rose' },
  { value: '#A8B8BF', name: 'Gris' },
];

interface CardModalProps {
  card: CardType;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (card: CardType) => void;
  onDelete: (cardId: number) => void;
  onArchive: (cardId: number, archived: boolean) => void;
  userRole?: string;
  boardId?: number;
  onRefresh?: () => void;
}

export const CardModal: React.FC<CardModalProps> = ({ card, isOpen, onClose, onUpdate, onDelete, onArchive, userRole = 'visitor', boardId, onRefresh }) => {
  const canEdit = (userRole === 'admin' || userRole === 'editor') && !card.archived;
  console.log('CardModal - userRole:', userRole, 'canEdit:', canEdit, 'archived:', card.archived);
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || '');
  const [labelColor, setLabelColor] = useState(card.label_color || '');
  const [startDate, setStartDate] = useState(card.start_date || '');
  const [dueDate, setDueDate] = useState(card.due_date || '');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [activities, setActivities] = useState<CardActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadComments();
      loadActivities();
      loadAttachments();
    }
  }, [isOpen, card.id]);

  const loadComments = async () => {
    setIsLoadingComments(true);
    try {
      const response = await commentApi.getAll(card.id);
      console.log('Comments loaded:', response.data);
      console.log('First comment user:', response.data[0]?.user);
      setComments(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des commentaires', err);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const loadActivities = async () => {
    setIsLoadingActivities(true);
    try {
      const response = await activityApi.getAll(card.id);
      setActivities(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des activités', err);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  const loadAttachments = async () => {
    setIsLoadingAttachments(true);
    try {
      const response = await attachmentApi.getAll(card.id);
      setAttachments(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des pièces jointes', err);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const handleAssignmentsChange = () => {
    // Recharger les activités
    loadActivities();
    // Rafraîchir les données du board pour mettre à jour les avatars
    onRefresh?.();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await commentApi.create(card.id, { content: newComment.trim() });
      setComments([response.data, ...comments]);
      setNewComment('');
    } catch (err) {
      console.error('Erreur lors de l\'ajout du commentaire', err);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const handleSaveComment = async (commentId: number) => {
    if (!editingCommentContent.trim()) return;

    try {
      const response = await commentApi.update(card.id, commentId, { content: editingCommentContent.trim() });
      setComments(comments.map(c => c.id === commentId ? response.data : c));
      setEditingCommentId(null);
      setEditingCommentContent('');
    } catch (err) {
      console.error('Erreur lors de la modification du commentaire', err);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce commentaire ?')) return;

    try {
      await commentApi.delete(card.id, commentId);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (err) {
      console.error('Erreur lors de la suppression du commentaire', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  if (!isOpen) return null;

  const handleSaveTitle = () => {
    if (title.trim()) {
      const truncatedTitle = title.trim().substring(0, 144);
      onUpdate({ ...card, title: truncatedTitle });
      setIsEditingTitle(false);
    }
  };

  const handleSaveDescription = () => {
    onUpdate({ ...card, description: description.trim() });
    setIsEditingDescription(false);
  };

  const handleSaveStartDate = (date: string) => {
    setStartDate(date);
    onUpdate({ ...card, start_date: date || undefined });
  };

  const handleSaveDueDate = (date: string) => {
    setDueDate(date);
    onUpdate({ ...card, due_date: date || undefined });
  };

  const handleColorChange = (color: string) => {
    const newColor = labelColor === color ? '' : color;
    setLabelColor(newColor);
    onUpdate({ ...card, label_color: newColor });
  };

  const handleDelete = () => {
    if (window.confirm('Voulez-vous vraiment supprimer cette carte ?')) {
      onDelete(card.id);
      onClose();
    }
  };

  const handleArchive = () => {
    const newArchivedState = !card.archived;
    const message = newArchivedState 
      ? 'Voulez-vous vraiment archiver cette carte ?' 
      : 'Voulez-vous vraiment désarchiver cette carte ?';
    
    if (window.confirm(message)) {
      onArchive(card.id, newArchivedState);
      onClose();
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?card=${card.id}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie du lien', err);
    }
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    // Fonction pour convertir hex en RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 150, g: 150, b: 150 };
    };

    // Couleur principale (couleur de la carte ou gris sobre)
    const mainColor = card.label_color ? hexToRgb(card.label_color) : { r: 150, g: 150, b: 150 };
    
    // En-tête coloré
    doc.setFillColor(mainColor.r, mainColor.g, mainColor.b);
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    // Titre centré sur l'en-tête avec gestion du retour à la ligne
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(card.title, pageWidth - 40);
    const titleStartY = titleLines.length > 1 ? 15 : 20;
    titleLines.forEach((line: string, index: number) => {
      const lineWidth = doc.getTextWidth(line);
      doc.text(line, (pageWidth - lineWidth) / 2, titleStartY + (index * 8));
    });
    
    // Statut archivé sur l'en-tête
    if (card.archived) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text('[ARCHIVÉE]', pageWidth - 30, 30);
    }
    
    yPosition = 45;
    doc.setTextColor(0, 0, 0);

    // Fonction pour dessiner un séparateur
    const drawSeparator = () => {
      doc.setDrawColor(mainColor.r, mainColor.g, mainColor.b);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 8;
    };

    // Fonction pour dessiner un titre de section
    const drawSectionTitle = (title: string) => {
      doc.setFillColor(mainColor.r, mainColor.g, mainColor.b, 0.1);
      doc.roundedRect(15, yPosition - 5, pageWidth - 30, 10, 2, 2, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(mainColor.r, mainColor.g, mainColor.b);
      doc.text(title, 20, yPosition + 2);
      doc.setTextColor(0, 0, 0);
      yPosition += 12;
    };

    // Section Affectations
    if (card.users && card.users.length > 0) {
      drawSectionTitle('AFFECTATIONS');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      card.users.forEach((user, index) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Puce et nom de l'utilisateur
        doc.setFillColor(mainColor.r, mainColor.g, mainColor.b);
        doc.circle(23, yPosition - 1, 1.5, 'F');
        doc.text(user.name, 28, yPosition);
        
        yPosition += 6;
      });
      
      yPosition += 5;
    }

    // Section Étiquettes
    if (card.labels && card.labels.length > 0) {
      drawSectionTitle('ETIQUETTES');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      let xPosition = 20;
      
      card.labels.forEach((label, index) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
          xPosition = 20;
        }
        
        const labelColor = hexToRgb(label.color);
        const labelWidth = doc.getTextWidth(label.name) + 8;
        
        // Vérifier si l'étiquette tient sur la ligne
        if (xPosition + labelWidth > pageWidth - 20) {
          yPosition += 8;
          xPosition = 20;
        }
        
        // Badge coloré
        doc.setFillColor(labelColor.r, labelColor.g, labelColor.b);
        doc.roundedRect(xPosition, yPosition - 4, labelWidth, 6, 1.5, 1.5, 'F');
        
        // Texte blanc
        doc.setTextColor(255, 255, 255);
        doc.text(label.name, xPosition + 4, yPosition);
        doc.setTextColor(0, 0, 0);
        
        xPosition += labelWidth + 5;
      });
      
      yPosition += 10;
    }

    // Description avec cadre et formatage Markdown
    if (card.description) {
      drawSectionTitle('DESCRIPTION');
      
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      
      const boxStartY = yPosition;
      let contentY = yPosition + 7;
      
      // Parser le Markdown ligne par ligne
      const lines = card.description.split('\n');
      
      lines.forEach((line) => {
        if (contentY > 270) {
          doc.addPage();
          contentY = 20;
        }
        
        // Titres
        if (line.match(/^#{1,3}\s/)) {
          const level = line.match(/^(#{1,3})/)?.[1].length || 1;
          const text = line.replace(/^#{1,6}\s/, '');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14 - level);
          doc.text(text, 25, contentY);
          contentY += 7;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          return;
        }
        
        // Listes à puces
        if (line.match(/^[-*]\s/)) {
          const text = line.replace(/^[-*]\s/, '');
          doc.text('• ' + text, 28, contentY);
          contentY += 5;
          return;
        }
        
        // Listes numérotées
        if (line.match(/^\d+\.\s/)) {
          doc.text(line, 28, contentY);
          contentY += 5;
          return;
        }
        
        // Ligne vide
        if (line.trim() === '') {
          contentY += 3;
          return;
        }
        
        // Texte normal avec gras et italique
        let currentX = 25;
        const parts = line.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);
        
        parts.forEach((part) => {
          if (!part) return;
          
          // Gras
          if (part.match(/^\*\*(.*?)\*\*$/)) {
            const text = part.replace(/\*\*/g, '');
            doc.setFont('helvetica', 'bold');
            doc.text(text, currentX, contentY);
            currentX += doc.getTextWidth(text);
            doc.setFont('helvetica', 'normal');
          }
          // Italique
          else if (part.match(/^\*(.*?)\*$/)) {
            const text = part.replace(/\*/g, '');
            doc.setFont('helvetica', 'italic');
            doc.text(text, currentX, contentY);
            currentX += doc.getTextWidth(text);
            doc.setFont('helvetica', 'normal');
          }
          // Code
          else if (part.match(/^`(.*?)`$/)) {
            const text = part.replace(/`/g, '');
            doc.setFont('courier', 'normal');
            doc.setFontSize(9);
            doc.text(text, currentX, contentY);
            currentX += doc.getTextWidth(text);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
          }
          // Texte normal
          else {
            const textLines = doc.splitTextToSize(part, pageWidth - 50 - (currentX - 25));
            textLines.forEach((textLine: string, idx: number) => {
              if (idx > 0) {
                contentY += 5;
                currentX = 25;
              }
              doc.text(textLine, currentX, contentY);
              if (idx === textLines.length - 1) {
                currentX += doc.getTextWidth(textLine);
              }
            });
          }
        });
        
        contentY += 5;
      });
      
      // Dessiner le cadre autour de tout le contenu
      const boxHeight = contentY - boxStartY + 3;
      doc.roundedRect(20, boxStartY, pageWidth - 40, boxHeight, 3, 3, 'S');
      
      yPosition = contentY + 7;
    }

    // Commentaires avec cadres individuels
    if (comments.length > 0) {
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      drawSectionTitle(`COMMENTAIRES (${comments.length})`);

      comments.forEach((comment) => {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        // Cadre du commentaire
        doc.setFillColor(245, 245, 245);
        const commentLines = doc.splitTextToSize(comment.content, pageWidth - 55);
        const commentBoxHeight = commentLines.length * 5 + 18;
        doc.roundedRect(20, yPosition, pageWidth - 40, commentBoxHeight, 3, 3, 'FD');
        
        // Auteur et date
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(mainColor.r, mainColor.g, mainColor.b);
        doc.text(`${comment.user?.name || 'Utilisateur'}`, 25, yPosition + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const commentDate = new Date(comment.created_at).toLocaleDateString('fr-FR');
        doc.text(commentDate, pageWidth - 50, yPosition + 6);
        
        // Contenu du commentaire
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.text(commentLines, 25, yPosition + 13);
        
        yPosition += commentBoxHeight + 5;
      });
      
      yPosition += 5;
    }

    // Activité récente en tableau
    if (activities.length > 0) {
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }

      drawSectionTitle('ACTIVITE RECENTE');

      // En-tête du tableau
      doc.setFillColor(mainColor.r, mainColor.g, mainColor.b);
      doc.rect(20, yPosition, pageWidth - 40, 8, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', 25, yPosition + 5);
      doc.text('Utilisateur', 60, yPosition + 5);
      doc.text('Action', 110, yPosition + 5);
      
      yPosition += 8;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');

      // Lignes du tableau
      activities.slice(0, 10).forEach((activity, index) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }

        // Alternance de couleur de fond
        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
          doc.rect(20, yPosition, pageWidth - 40, 7, 'F');
        }

        const activityDate = new Date(activity.created_at).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        doc.setFontSize(8);
        doc.text(activityDate, 25, yPosition + 5);
        doc.text(activity.user?.name || 'Utilisateur', 60, yPosition + 5);
        
        const actionText = doc.splitTextToSize(activity.action, 80);
        doc.text(actionText[0], 110, yPosition + 5);
        
        yPosition += 7;
      });

      // Bordure du tableau
      doc.setDrawColor(mainColor.r, mainColor.g, mainColor.b);
      doc.setLineWidth(0.5);
      doc.rect(20, yPosition - (activities.slice(0, 10).length * 7) - 8, pageWidth - 40, (activities.slice(0, 10).length * 7) + 8);
      
      yPosition += 10;
    }

    // Pièces jointes
    if (attachments.length > 0) {
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      drawSectionTitle(`PIECES JOINTES (${attachments.length})`);

      attachments.forEach((attachment, index) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }

        // Icône selon le type de fichier
        let fileIcon = '';
        switch (attachment.file_type) {
          case 'image':
            fileIcon = '[IMG]';
            break;
          case 'video':
            fileIcon = '[VID]';
            break;
          case 'document':
            fileIcon = '[DOC]';
            break;
          case 'archive':
            fileIcon = '[ZIP]';
            break;
          default:
            fileIcon = '[FILE]';
        }

        // Ligne du fichier
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fileIcon} ${attachment.filename}`, 25, yPosition);
        
        // Taille du fichier à droite
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(attachment.formatted_size, pageWidth - 40, yPosition);
        doc.setTextColor(0, 0, 0);
        
        yPosition += 6;
      });

      yPosition += 10;
    }

    // Pied de page avec dates
    const footerY = pageHeight - 15;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, footerY - 5, pageWidth - 20, footerY - 5);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Créée le: ${new Date(card.created_at).toLocaleDateString('fr-FR')}`, 20, footerY);
    doc.text(`Modifiée le: ${new Date(card.updated_at).toLocaleDateString('fr-FR')}`, pageWidth - 60, footerY);

    // Télécharger le PDF
    const fileName = `carte-${card.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    doc.save(fileName);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (action: string) => {
    const iconProps = { width: 16, height: 16, strokeWidth: 2 };
    switch (action) {
      case 'created':
        return <Plus {...iconProps} color="#0079BF" />;
      case 'title_changed':
        return <EditPencil {...iconProps} color="#FF991F" />;
      case 'description_changed':
        return <Page {...iconProps} color="#6554C0" />;
      case 'moved':
        return <Check {...iconProps} color="#00875A" />;
      case 'archived':
        return <BoxIso {...iconProps} color="#FF991F" />;
      case 'unarchived':
        return <BoxIso {...iconProps} color="#00875A" />;
      case 'commented':
        return <Xmark {...iconProps} color="#5E6C84" />;
      default:
        return <Page {...iconProps} color="#5E6C84" />;
    }
  };

  const getActivityText = (activity: CardActivity) => {
    const userName = activity.user?.name || 'Utilisateur inconnu';
    
    switch (activity.action) {
      case 'created':
        return <><strong>{userName}</strong> a créé cette carte</>;
      case 'title_changed':
        return (
          <>
            <strong>{userName}</strong> a modifié le titre
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                <span className="activity-old">"{activity.old_value}"</span>
                {' → '}
                <span className="activity-new">"{activity.new_value}"</span>
              </div>
            )}
          </>
        );
      case 'description_changed':
        return <><strong>{userName}</strong> a modifié la description</>;
      case 'moved':
        return (
          <>
            <strong>{userName}</strong> a déplacé la carte
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                de <span className="activity-old">{activity.old_value}</span>
                {' vers '}
                <span className="activity-new">{activity.new_value}</span>
              </div>
            )}
          </>
        );
      case 'archived':
        return <><strong>{userName}</strong> a archivé cette carte</>;
      case 'unarchived':
        return <><strong>{userName}</strong> a désarchivé cette carte</>;
      case 'commented':
        return <><strong>{userName}</strong> a ajouté un commentaire</>;
      case 'assigned_user':
        return (
          <>
            <strong>{userName}</strong> a affecté la carte à <strong>{activity.new_value}</strong>
          </>
        );
      case 'unassigned_user':
        return (
          <>
            <strong>{userName}</strong> a retiré <strong>{activity.old_value}</strong> de la carte
          </>
        );
      case 'added_label':
        return (
          <>
            <strong>{userName}</strong> a ajouté l'étiquette{' '}
            <span 
              className="activity-label-badge" 
              style={{ backgroundColor: activity.metadata?.label_color || '#ccc' }}
            >
              {activity.new_value}
            </span>
          </>
        );
      case 'removed_label':
        return (
          <>
            <strong>{userName}</strong> a supprimé l'étiquette{' '}
            <span 
              className="activity-label-badge" 
              style={{ backgroundColor: activity.metadata?.label_color || '#ccc' }}
            >
              {activity.old_value}
            </span>
          </>
        );
      case 'updated_label':
        return (
          <>
            <strong>{userName}</strong> a modifié l'étiquette
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                <span className="activity-old">"{activity.old_value}"</span>
                {' → '}
                <span className="activity-new">"{activity.new_value}"</span>
              </div>
            )}
          </>
        );
      default:
        return <><strong>{userName}</strong> a effectué une action</>;
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container">
        {labelColor && (
          <div className="card-label-banner" style={{ backgroundColor: labelColor }}>
            {canEdit && (
              <button 
                className="btn-color-picker"
                onClick={() => setShowColorPalette(!showColorPalette)}
                title="Changer la couleur"
              >
                <EditPencil width={20} height={20} strokeWidth={2} color="white" />
              </button>
            )}
          </div>
        )}
        {!labelColor && canEdit && (
          <div className="card-label-banner-empty">
            <button 
              className="btn-color-picker"
              onClick={() => setShowColorPalette(!showColorPalette)}
              title="Ajouter une couleur"
            >
              <Plus width={20} height={20} strokeWidth={2} />
            </button>
          </div>
        )}
        
        {/* Palette de couleurs déroulante */}
        {showColorPalette && canEdit && (
          <div className="color-palette-dropdown">
            <div className="color-palette-header">
              <h4>Couleur de la carte</h4>
              <button onClick={() => setShowColorPalette(false)}>
                <Xmark width={16} height={16} strokeWidth={2} />
              </button>
            </div>
            <div className="card-color-palette">
              {LABEL_COLORS.map((color) => (
                <button
                  key={color.value}
                  className={`card-color-option ${labelColor === color.value ? 'selected' : ''}`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => {
                    handleColorChange(color.value);
                    setShowColorPalette(false);
                  }}
                  title={color.name}
                >
                  {labelColor === color.value && (
                    <Check width={18} height={18} strokeWidth={2} color="white" />
                  )}
                </button>
              ))}
              {labelColor && (
                <button
                  className="card-color-remove"
                  onClick={() => {
                    handleColorChange('');
                    setShowColorPalette(false);
                  }}
                  title="Retirer la couleur"
                >
                  <Xmark width={16} height={16} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="modal-header-buttons">
          <button 
            className="modal-pdf" 
            onClick={handleGeneratePDF} 
            title="Télécharger en PDF"
          >
            <Download width={20} height={20} strokeWidth={2} />
          </button>
          <button 
            className="modal-link" 
            onClick={handleCopyLink} 
            title="Copier le lien"
          >
            {showCopiedFeedback ? (
              <Check width={20} height={20} strokeWidth={2} />
            ) : (
              <Link width={20} height={20} strokeWidth={2} />
            )}
          </button>
          {(userRole === 'admin' || userRole === 'editor') && (
            <button 
              className="modal-archive" 
              onClick={handleArchive} 
              title={card.archived ? "Désarchiver la carte" : "Archiver la carte"}
            >
              <BoxIso width={20} height={20} strokeWidth={2} />
            </button>
          )}
          {canEdit && (
            <button className="modal-delete" onClick={handleDelete} title="Supprimer la carte">
              <Trash width={20} height={20} strokeWidth={2} />
            </button>
          )}
          <button className="modal-close" onClick={onClose} title="Fermer">
            <Xmark width={20} height={20} strokeWidth={2} />
          </button>
        </div>
        
        <div className="modal-content">
          {/* Section gauche */}
          <div className="modal-left">
            {/* Titre */}
            <div className="modal-header">
              {isEditingTitle ? (
                <div className="modal-title-edit">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') {
                        setTitle(card.title);
                        setIsEditingTitle(false);
                      }
                    }}
                    maxLength={144}
                    autoFocus
                    className="modal-title-input"
                  />
                </div>
              ) : (
                <h2 
                  className="modal-title" 
                  onClick={() => canEdit && setIsEditingTitle(true)}
                  style={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  <span className="modal-title-icon">○</span>
                  {card.title}
                </h2>
              )}
            </div>

            {/* Message pour les cartes archivées */}
            {card.archived && (
              <div className="modal-archived-notice">
                <BoxIso width={18} height={18} strokeWidth={2} />
                <span>Cette carte est archivée. Désarchivez-la pour pouvoir la modifier.</span>
              </div>
            )}

            {/* Membres affectés */}
            {canEdit && boardId && (
              <div className="modal-section">
                <CardAssignments 
                  cardId={card.id} 
                  boardId={boardId} 
                  onAssignmentsChange={handleAssignmentsChange} 
                />
              </div>
            )}

            {/* Étiquettes */}
            {canEdit && (
              <div className="modal-section">
                <LabelManager 
                  cardId={card.id} 
                  boardId={boardId} 
                  onLabelsChange={() => {
                    loadActivities();
                    onRefresh?.();
                  }} 
                />
              </div>
            )}

            {/* Dates */}
            <div className="modal-section">
              <h3>Dates</h3>
              <div className="modal-dates">
                <div className="date-field">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleSaveStartDate(e.target.value)}
                    disabled={!canEdit}
                    className="date-input"
                  />
                </div>
                <div className="date-field">
                  <label>Date d'échéance</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => handleSaveDueDate(e.target.value)}
                    disabled={!canEdit}
                    className="date-input"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="modal-section">
              <div className="modal-section-header">
                <h3>Description</h3>
                {!isEditingDescription && canEdit && (
                  <button 
                    className="btn-edit-section"
                    onClick={() => setIsEditingDescription(true)}
                  >
                    Modifier
                  </button>
                )}
              </div>
              
              {isEditingDescription ? (
                <div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ajouter une description plus détaillée..."
                    className="modal-textarea"
                    rows={6}
                    autoFocus
                  />
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={handleSaveDescription}>
                      Enregistrer
                    </button>
                    <button 
                      className="btn btn-cancel" 
                      onClick={() => {
                        setDescription(card.description || '');
                        setIsEditingDescription(false);
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="modal-description"
                  onClick={() => canEdit && setIsEditingDescription(true)}
                  style={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  {description ? (
                    <ReactMarkdown>{description}</ReactMarkdown>
                  ) : (
                    <span className="description-placeholder">
                      {canEdit ? 'Ajouter une description plus détaillée...' : 'Aucune description'}
                    </span>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Section droite - Activité */}
          <div className="modal-right">
            <h3 className="modal-sidebar-title">Activité</h3>
            
            <div className="modal-activity">
              {isLoadingActivities ? (
                <div className="activity-loading">Chargement des activités...</div>
              ) : activities.length > 0 ? (
                activities.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">{getActivityIcon(activity.action)}</div>
                    <div className="activity-content">
                      <div className="activity-text">
                        {getActivityText(activity)}
                      </div>
                      <div className="activity-date">
                        {formatDate(activity.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-empty">Aucune activité pour le moment</div>
              )}

              {/* Pièces jointes */}
              <AttachmentList cardId={card.id} canEdit={canEdit} />

              <div className="modal-section">
                <h4 className="modal-subsection-title">Commentaires</h4>
                
                {/* Formulaire d'ajout de commentaire */}
                {!card.archived && (
                  <div className="comment-form">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Écrire un commentaire..."
                      className="modal-comment-input"
                      rows={3}
                    />
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                    >
                      Commenter
                    </button>
                  </div>
                )}

                {/* Liste des commentaires */}
                <div className="comments-list">
                  {isLoadingComments ? (
                    <div className="comment-loading">Chargement des commentaires...</div>
                  ) : comments.length === 0 ? (
                    <div className="comment-empty">Aucun commentaire pour le moment</div>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <div className="comment-author">
                            <div className="comment-avatar">
                              {(() => {
                                console.log('Comment user data:', comment.user);
                                console.log('Avatar URL:', comment.user?.avatar_url);
                                return comment.user?.avatar_url ? (
                                  <img src={comment.user.avatar_url} alt={comment.user.name} className="comment-avatar-image" />
                                ) : (
                                  comment.user?.name?.charAt(0).toUpperCase() || '💬'
                                );
                              })()}
                            </div>
                            <div>
                              <div className="comment-author-name">
                                {comment.user?.name || 'Utilisateur inconnu'}
                              </div>
                              <div className="comment-date">{formatDate(comment.created_at)}</div>
                            </div>
                          </div>
                          <div className="comment-actions">
                            {editingCommentId !== comment.id && !card.archived && (
                              <>
                                <button 
                                  className="btn-icon"
                                  onClick={() => handleEditComment(comment)}
                                  title="Modifier"
                                >
                                  <EditPencil width={16} height={16} strokeWidth={2} />
                                </button>
                                <button 
                                  className="btn-icon"
                                  onClick={() => handleDeleteComment(comment.id)}
                                  title="Supprimer"
                                >
                                  <Trash width={16} height={16} strokeWidth={2} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {editingCommentId === comment.id ? (
                          <div className="comment-edit">
                            <textarea
                              value={editingCommentContent}
                              onChange={(e) => setEditingCommentContent(e.target.value)}
                              className="modal-comment-input"
                              rows={3}
                              autoFocus
                            />
                            <div className="modal-actions">
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => handleSaveComment(comment.id)}
                              >
                                Enregistrer
                              </button>
                              <button 
                                className="btn btn-cancel btn-sm"
                                onClick={handleCancelEdit}
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="comment-content">
                            {comment.content}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
